import React, { useState, useEffect } from "react";
import { ScreenerView } from "../../model/careScreenerModels";
import { IS_DEV } from "../../utils/constants";

interface CareScreenerDebugPanelProps {
  currentView: ScreenerView;
  onViewChange: (view: ScreenerView) => void;
  onCareScreenerSubmissionIdChange?: (id: number) => void;
  onTypeformResponseIdChange?: (id: string) => void;
  typeformId?: string;
  typeformPath?: string;
  currentCareScreenerSubmissionId?: number;
  currentTypeformResponseId?: string;
}

export default function CareScreenerDebugPanel({
  currentView,
  onViewChange,
  onCareScreenerSubmissionIdChange,
  onTypeformResponseIdChange,
  typeformId,
  typeformPath,
  currentCareScreenerSubmissionId,
  currentTypeformResponseId
}: CareScreenerDebugPanelProps) {
  const [debugPanelCollapsed, setDebugPanelCollapsed] = useState(false);
  const [typeformResponseId, setTypeformResponseId] = useState("");
  const [careScreenerSubmissionId, setCareScreenerSubmissionId] = useState("");
  const [sessionStorageKey, setSessionStorageKey] = useState("");
  const [sessionStorageValue, setSessionStorageValue] = useState("");
  const [sessionStorageItems, setSessionStorageItems] = useState<Record<string, string>>({});

  const refreshSessionStorage = () => {
    const items: Record<string, string> = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        items[key] = sessionStorage.getItem(key) || "";
      }
    }
    setSessionStorageItems(items);
  };

  const setSessionStorageItem = () => {
    if (sessionStorageKey.trim()) {
      sessionStorage.setItem(sessionStorageKey.trim(), sessionStorageValue);
      refreshSessionStorage();
      console.log(`Debug: Set sessionStorage[${sessionStorageKey}] = ${sessionStorageValue}`);
    }
  };

  const removeSessionStorageItem = (key: string) => {
    sessionStorage.removeItem(key);
    refreshSessionStorage();
    console.log(`Debug: Removed sessionStorage[${key}]`);
  };

  const clearAllSessionStorage = () => {
    sessionStorage.clear();
    refreshSessionStorage();
    console.log("Debug: Cleared all sessionStorage");
  };

  useEffect(() => {
    refreshSessionStorage();
  }, []);

  const handleCareScreenerSubmissionIdChange = () => {
    const id = parseInt(careScreenerSubmissionId);
    if (!isNaN(id) && onCareScreenerSubmissionIdChange) {
      onCareScreenerSubmissionIdChange(id);
      console.log(`Debug: Set care screener submission ID: ${id}`);
    }
  };

  const handleTypeformResponseIdChange = () => {
    if (typeformResponseId.trim() && onTypeformResponseIdChange) {
      onTypeformResponseIdChange(typeformResponseId.trim());
      console.log(`Debug: Set typeform response ID: ${typeformResponseId}`);
    }
  };

  if (!IS_DEV) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 10,
        left: 10,
        zIndex: 1000,
        background: "rgba(0,0,0,0.8)",
        padding: "10px",
        borderRadius: "5px",
        color: "white",
        fontSize: "12px",
        maxWidth: debugPanelCollapsed ? "150px" : "400px",
        transition: "max-width 0.3s ease"
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: debugPanelCollapsed ? "0" : "8px"
        }}
      >
        <div style={{ fontWeight: "bold" }}>
          Current: {currentView}
          {typeformPath && (
            <div style={{ fontSize: "10px", opacity: 0.8 }}>
              Typeform: {typeformPath} ({typeformId})
            </div>
          )}
        </div>
        <button
          onClick={() => setDebugPanelCollapsed(!debugPanelCollapsed)}
          style={{
            background: "none",
            border: "none",
            color: "white",
            cursor: "pointer",
            fontSize: "14px",
            padding: "2px",
            marginLeft: "8px"
          }}
        >
          {debugPanelCollapsed ? "▶" : "▼"}
        </button>
      </div>
      {!debugPanelCollapsed && (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
            {Object.values(ScreenerView).map(viewName => (
              <button
                key={viewName}
                onClick={() => onViewChange(viewName)}
                style={{
                  fontSize: "10px",
                  padding: "4px 8px",
                  margin: "1px",
                  backgroundColor: currentView === viewName ? "#4CAF50" : "#666",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer"
                }}
              >
                {viewName}
              </button>
            ))}
          </div>

          <div style={{ borderTop: "1px solid #555", paddingTop: "8px", marginTop: "8px" }}>
            <div style={{ fontSize: "10px", marginBottom: "4px", fontWeight: "bold" }}>SessionStorage:</div>

            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "9px", marginBottom: "4px", opacity: 0.8 }}>Current Items:</div>
              {Object.keys(sessionStorageItems).length === 0 ? (
                <div style={{ fontSize: "9px", opacity: 0.6, fontStyle: "italic" }}>No items</div>
              ) : (
                Object.entries(sessionStorageItems).map(([key, value]) => (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "2px" }}>
                    <span style={{ fontSize: "9px", color: "#4CAF50", minWidth: "80px" }}>{key}:</span>
                    <span style={{ fontSize: "9px", opacity: 0.8, flex: 1, wordBreak: "break-all" }}>
                      {value.length > 30 ? `${value.substring(0, 30)}...` : value}
                    </span>
                    <button
                      onClick={() => removeSessionStorageItem(key)}
                      style={{
                        fontSize: "8px",
                        padding: "2px 4px",
                        backgroundColor: "#f44336",
                        color: "white",
                        border: "none",
                        borderRadius: "2px",
                        cursor: "pointer"
                      }}
                    >
                      x
                    </button>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginBottom: "8px" }}>
              <div style={{ fontSize: "9px", marginBottom: "4px", opacity: 0.8 }}>Set Item:</div>
              <div style={{ display: "flex", gap: "4px", alignItems: "center", marginBottom: "4px" }}>
                <input
                  type="text"
                  value={sessionStorageKey}
                  onChange={e => setSessionStorageKey(e.target.value)}
                  placeholder="Key..."
                  style={{
                    flex: 1,
                    padding: "4px 6px",
                    fontSize: "10px",
                    border: "1px solid #666",
                    borderRadius: "3px",
                    backgroundColor: "#333",
                    color: "white",
                    minWidth: "60px"
                  }}
                />
                <input
                  type="text"
                  value={sessionStorageValue}
                  onChange={e => setSessionStorageValue(e.target.value)}
                  placeholder="Value..."
                  style={{
                    flex: 1,
                    padding: "4px 6px",
                    fontSize: "10px",
                    border: "1px solid #666",
                    borderRadius: "3px",
                    backgroundColor: "#333",
                    color: "white",
                    minWidth: "60px"
                  }}
                />
                <button
                  onClick={setSessionStorageItem}
                  disabled={!sessionStorageKey.trim()}
                  style={{
                    fontSize: "10px",
                    padding: "4px 8px",
                    backgroundColor: sessionStorageKey.trim() ? "#4CAF50" : "#666",
                    color: "white",
                    border: "none",
                    borderRadius: "3px",
                    cursor: sessionStorageKey.trim() ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap"
                  }}
                >
                  Set
                </button>
              </div>
            </div>

            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={refreshSessionStorage}
                style={{
                  fontSize: "10px",
                  padding: "4px 8px",
                  backgroundColor: "#2196F3",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                  whiteSpace: "nowrap"
                }}
              >
                Refresh
              </button>
              <button
                onClick={clearAllSessionStorage}
                style={{
                  fontSize: "10px",
                  padding: "4px 8px",
                  backgroundColor: "#f44336",
                  color: "white",
                  border: "none",
                  borderRadius: "3px",
                  cursor: "pointer",
                  whiteSpace: "nowrap"
                }}
              >
                Clear All
              </button>
            </div>
          </div>

          {(onCareScreenerSubmissionIdChange || onTypeformResponseIdChange) && (
            <div style={{ borderTop: "1px solid #555", paddingTop: "8px", marginTop: "8px" }}>
              <div style={{ fontSize: "10px", marginBottom: "4px", fontWeight: "bold" }}>Set State Values:</div>

              {onCareScreenerSubmissionIdChange && (
                <div style={{ marginBottom: "6px" }}>
                  <div style={{ fontSize: "9px", marginBottom: "2px", opacity: 0.8 }}>
                    Care Screener Submission ID{" "}
                    {currentCareScreenerSubmissionId && `(Current: ${currentCareScreenerSubmissionId})`}:
                  </div>
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <input
                      type="number"
                      value={careScreenerSubmissionId}
                      onChange={e => setCareScreenerSubmissionId(e.target.value)}
                      placeholder="Enter ID..."
                      style={{
                        flex: 1,
                        padding: "4px 6px",
                        fontSize: "10px",
                        border: "1px solid #666",
                        borderRadius: "3px",
                        backgroundColor: "#333",
                        color: "white",
                        minWidth: "80px"
                      }}
                    />
                    <button
                      onClick={handleCareScreenerSubmissionIdChange}
                      disabled={!careScreenerSubmissionId.trim()}
                      style={{
                        fontSize: "10px",
                        padding: "4px 8px",
                        backgroundColor: careScreenerSubmissionId.trim() ? "#FF9800" : "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        cursor: careScreenerSubmissionId.trim() ? "pointer" : "not-allowed",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Set
                    </button>
                  </div>
                </div>
              )}

              {onTypeformResponseIdChange && (
                <div style={{ marginBottom: "6px" }}>
                  <div style={{ fontSize: "9px", marginBottom: "2px", opacity: 0.8 }}>
                    Typeform Response ID {currentTypeformResponseId && `(Current: ${currentTypeformResponseId})`}:
                  </div>
                  <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                    <input
                      type="text"
                      value={typeformResponseId}
                      onChange={e => setTypeformResponseId(e.target.value)}
                      placeholder="Enter response ID..."
                      style={{
                        flex: 1,
                        padding: "4px 6px",
                        fontSize: "10px",
                        border: "1px solid #666",
                        borderRadius: "3px",
                        backgroundColor: "#333",
                        color: "white",
                        minWidth: "120px"
                      }}
                    />
                    <button
                      onClick={handleTypeformResponseIdChange}
                      disabled={!typeformResponseId.trim()}
                      style={{
                        fontSize: "10px",
                        padding: "4px 8px",
                        backgroundColor: typeformResponseId.trim() ? "#9C27B0" : "#666",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        cursor: typeformResponseId.trim() ? "pointer" : "not-allowed",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Set
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
